import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { emptyDraft, type PasswordChangeDraft } from '../domain/password-change.model';
import type { PasswordChangeError } from '../domain/password-change-error';
import { initialState } from './password-change.state';

/**
 * Password-change slice. Reducers are pure state transitions only; the POST
 * lives in the saga, which dispatches these lifecycle transitions as it runs.
 *
 * ## The rule to keep
 *
 * Every path that leaves the form (`closed`, `submitSucceeded`, `reset`) puts
 * `draft` back to `emptyDraft`. Two passwords in clear must not survive the
 * moment they were needed: the store is not persisted, but it is readable from
 * the devtools and from any code holding the store, and there is no reason to
 * keep them one render longer. If you add a branch, wipe the draft in it too.
 */
const slice = createSlice({
  name: 'passwordChange',
  initialState,
  reducers: {
    /** Open the form (from the collapsed "Password / Change" row). */
    opened: (state) => {
      state.open = true;
      state.draft = emptyDraft;
      state.validationErrors = {};
      state.error = null;
    },
    /** Close without submitting; discards the typed secrets. */
    closed: () => initialState,

    /** A single field changed; clears that field's error so it stops shouting mid-fix. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof PasswordChangeDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
      if (state.submitStatus === 'failed') {
        state.submitStatus = 'idle';
        state.error = null;
      }
    },

    // ── Submit lifecycle (dispatched by the saga) ──
    submitStarted: (state) => {
      state.submitStatus = 'submitting';
      state.error = null;
      state.validationErrors = {};
    },
    /**
     * Success closes the form and wipes the draft, keeping only the count the
     * page needs to phrase its message.
     */
    submitSucceeded: (state, action: PayloadAction<{ revokedSessions: number }>) => {
      state.submitStatus = 'succeeded';
      state.open = false;
      state.draft = emptyDraft;
      state.validationErrors = {};
      state.error = null;
      state.revokedSessions = action.payload.revokedSessions;
    },
    submitFailed: (state, action: PayloadAction<PasswordChangeError>) => {
      state.submitStatus = 'failed';
      state.error = action.payload;
      state.validationErrors =
        action.payload.kind === 'validation' ? action.payload.fields : {};
    },
    /** The page handled the success (notified, refreshed sessions); back to idle. */
    submitAcknowledged: (state) => {
      state.submitStatus = 'idle';
      state.revokedSessions = null;
    },

    /** Reset everything (on unmount / navigation away). */
    reset: () => initialState,
  },
});

export const passwordChangeActions = slice.actions;
export const passwordChangeReducer = slice.reducer;
